/**
 * Typing Audio Manager
 *
 * Manages audio playback for typing sounds with performance optimizations
 * and multiple sound variations similar to WriteRush implementation.
 */

import { createHeartbeatSound } from '@/utils/create-heartbeat-sound';

export interface TypingSoundConfig {
  name: string;
  url: string;
  enabled: boolean;
}

export interface AudioManagerOptions {
  fastMode: boolean; // Reuse audio objects for better performance
  volume: number; // 0-1
  playbackRateVariation: boolean; // Add random playback rate variation
}

export class TypingAudioManager {
  private audioContext: AudioContext | null = null;
  private audioBuffers = new Map<string, AudioBuffer>();
  private fastModeAudio = new Map<string, HTMLAudioElement>();
  private options: AudioManagerOptions;
  private isInitialized = false;

  constructor(options: Partial<AudioManagerOptions> = {}) {
    this.options = {
      fastMode: true,
      volume: 0.5,
      playbackRateVariation: true,
      ...options
    };
  }

  /**
   * Initialize the audio system
   * Must be called after user interaction due to browser autoplay policies
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Initialize Web Audio API for better performance
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Resume context if suspended (required by some browsers)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      this.isInitialized = true;
    } catch (error) {
      console.warn('Web Audio API not available, falling back to HTML Audio:', error);
      this.isInitialized = true;
    }
  }

  /**
   * Load a sound file for typing effects
   */
  async loadSound(config: TypingSoundConfig): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      let audioUrl = config.url;
      let useGeneratedSound = false;

      // Special handling for heartbeat sound - check if we need to use programmatic fallback
      if (config.name === 'heartbeat' && config.url === '/audio/typing/heartbeat.mp3') {
        try {
          const response = await fetch(config.url);
          if (!response.ok) {
            throw new Error('Heartbeat file not found');
          }
          console.log('✅ Heartbeat audio file found and loaded successfully!');
        } catch {
          console.log('⚠️ Heartbeat audio file not found, using programmatic sound');
          audioUrl = createHeartbeatSound();
          useGeneratedSound = true;
        }
      }

      if (this.options.fastMode) {
        // Create reusable HTML Audio element for fast mode
        const audio = new Audio(audioUrl);
        audio.preload = 'auto';
        audio.volume = this.options.volume;

        // For generated sounds, we need to handle them differently
        if (useGeneratedSound && config.name === 'heartbeat') {
          // Lower volume for generated heartbeat sound
          audio.volume = this.options.volume * 0.6;
        }

        this.fastModeAudio.set(config.name, audio);
      }

      // Also load into Web Audio API buffer for higher quality mode
      if (this.audioContext && !useGeneratedSound) {
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        this.audioBuffers.set(config.name, audioBuffer);
      }
    } catch (error) {
      console.error(`Failed to load sound ${config.name}:`, error);

      // If heartbeat fails completely, fall back to soft click
      if (config.name === 'heartbeat') {
        console.log('Falling back to soft click for heartbeat sound');
        try {
          const fallbackAudio = new Audio('/audio/typing/click5.mp3');
          fallbackAudio.preload = 'auto';
          fallbackAudio.volume = this.options.volume * 0.7; // Slightly quieter
          this.fastModeAudio.set(config.name, fallbackAudio);
        } catch (fallbackError) {
          console.error('Fallback sound also failed:', fallbackError);
        }
      }
    }
  }

  /**
   * Play a typing sound with optional variations
   */
  playSound(soundName: string): void {
    if (!this.isInitialized) return;

    try {
      if (this.options.fastMode && this.fastModeAudio.has(soundName)) {
        this.playFastModeSound(soundName);
      } else if (this.audioContext && this.audioBuffers.has(soundName)) {
        this.playWebAudioSound(soundName);
      }
    } catch (error) {
      console.error(`Failed to play sound ${soundName}:`, error);
    }
  }

  /**
   * Play sound using HTML Audio (fast mode)
   */
  private playFastModeSound(soundName: string): void {
    const audio = this.fastModeAudio.get(soundName);
    if (!audio) return;

    // Reset to beginning for rapid typing
    audio.currentTime = 0;
    
    // Add playback rate variation for more natural feel
    if (this.options.playbackRateVariation) {
      audio.playbackRate = Math.random() * 1.6 + 0.4; // 0.4x to 2.0x speed
    }

    audio.play().catch(error => {
      // Ignore play errors (common when typing very fast)
      if (error.name !== 'AbortError') {
        console.warn('Audio play failed:', error);
      }
    });
  }

  /**
   * Play sound using Web Audio API (higher quality)
   */
  private playWebAudioSound(soundName: string): void {
    if (!this.audioContext) return;

    const buffer = this.audioBuffers.get(soundName);
    if (!buffer) return;

    const source = this.audioContext.createBufferSource();
    const gainNode = this.audioContext.createGain();

    source.buffer = buffer;
    gainNode.gain.value = this.options.volume;

    // Add playback rate variation
    if (this.options.playbackRateVariation) {
      source.playbackRate.value = Math.random() * 1.6 + 0.4;
    }

    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    source.start();
  }

  /**
   * Update audio options
   */
  updateOptions(newOptions: Partial<AudioManagerOptions>): void {
    this.options = { ...this.options, ...newOptions };

    // Update volume for fast mode audio elements
    if (newOptions.volume !== undefined) {
      this.fastModeAudio.forEach(audio => {
        audio.volume = newOptions.volume!;
      });
    }
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    this.audioBuffers.clear();
    this.fastModeAudio.clear();
    
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }
}

/**
 * Default typing sounds configuration
 */
export const DEFAULT_TYPING_SOUNDS: TypingSoundConfig[] = [
  { name: 'none', url: '', enabled: true },
  { name: 'typewriter1', url: '/audio/typing/click1.mp3', enabled: true },
  { name: 'typewriter2', url: '/audio/typing/click2.mp3', enabled: true },
  { name: 'typewriter3', url: '/audio/typing/click3.mp3', enabled: true },
  { name: 'mechanical', url: '/audio/typing/click4.mp3', enabled: true },
  { name: 'soft', url: '/audio/typing/click5.mp3', enabled: true },
  { name: 'heartbeat', url: '/audio/typing/heartbeat.mp3', enabled: true },
];
